import { useMutation, useQueryClient, type QueryKey, type UseMutationResult } from '@tanstack/react-query'
import { ApiError } from '@/lib/api-client'
import { useToast } from '@/components/ui/useToast'

interface AdminMutationOptions<TVariables, TData> {
  mutationFn: (variables: TVariables) => Promise<TData>
  /** Shown on success unless the server sent its own message. */
  successTitle: string
  successDescription?: (data: TData, variables: TVariables) => string | undefined
  /** Everything the write could have changed. */
  invalidate: QueryKey[]
  errorTitle?: string
  onSuccess?: (data: TData, variables: TVariables) => void
}

interface MaybeMessage {
  message?: unknown
}

/**
 * One place where every admin write is wired up.
 *
 * A mutation always: reports the outcome to the operator, invalidates the lists
 * its write could have changed so nothing on screen is stale, and turns a
 * failure into a sentence rather than a silent no-op.
 */
export function useAdminMutation<TVariables, TData>(
  options: AdminMutationOptions<TVariables, TData>,
): UseMutationResult<TData, unknown, TVariables> {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation<TData, unknown, TVariables>({
    mutationFn: options.mutationFn,
    onSuccess: (data, variables) => {
      // The API's own wording is more specific than anything invented here —
      // "Trip request cancelled. If paid by card, refund has been initiated."
      const serverMessage = (data as MaybeMessage | null)?.message
      const description =
        options.successDescription?.(data, variables) ??
        (typeof serverMessage === 'string' && serverMessage.trim().length > 0 ? serverMessage.trim() : undefined)

      toast.success(options.successTitle, description)

      for (const key of options.invalidate) {
        void queryClient.invalidateQueries({ queryKey: key })
      }

      options.onSuccess?.(data, variables)
    },
    onError: (error) => {
      const title = options.errorTitle ?? 'Action failed'

      if (error instanceof ApiError) {
        // A 401 has already dropped the session; the route guard takes over and
        // a toast about it would just be noise behind the login screen.
        if (error.isUnauthorized) return
        toast.error(title, error.message)
        return
      }
      toast.error(title, error instanceof Error ? error.message : 'An unexpected error occurred.')
    },
  })
}
